import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Injector, Input, Output, SimpleChanges, } from '@angular/core';
import { AbstractComponent, Attendance, AttendanceService, AttendanceStatusEnum, Channel, channelIconOf, MESSAGE_TYPE_LABELS, MONITORING_DELAY_THRESHOLD_SECONDS, MonitoringColumn, Pager, STATUS_ICONS, STATUS_LABELS, } from 'lib-trend-core';
import { finalize, takeUntil } from 'rxjs';

export interface ColumnMetrics {
  type: 'status' | 'user' | 'department';
  columnKey: string;
  total: number;
  delayed: number;
}

const RELOAD_KEYS = ['column', 'channelFilter', 'reloadTrigger'] as const;
const RECOMPUTE_KEYS = ['delayThresholdSeconds', 'tickTrigger'] as const;

@Component({
    selector: 'app-attendance-monitoring-column',
    templateUrl: './attendance-monitoring-column.component.html',
    styleUrls: ['./attendance-monitoring-column.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AttendanceMonitoringColumnComponent extends AbstractComponent {
  @Input() column!: MonitoringColumn;
  @Input() channelFilter: string | null = null;
  @Input() reloadTrigger = 0;
  @Input() tickTrigger = 0;
  @Input() delayThresholdSeconds: number = MONITORING_DELAY_THRESHOLD_SECONDS;

  @Output() metricsChanged = new EventEmitter<ColumnMetrics>();

  pager: Pager<Attendance> = new Pager<Attendance>({ perPage: 10 });
  delayedCount = 0;

  constructor(
    injector: Injector,
    private attendanceService: AttendanceService,
    private cdr: ChangeDetectorRef,
  ) {
    super(injector);
  }

  ngOnInit(): void { this.loadAttendances(); }

  ngOnChanges(changes: SimpleChanges): void {
    const shouldReload = RELOAD_KEYS.some(k => changes[k] && !changes[k].firstChange);
    if (shouldReload) {
      this.pager.page = 1;
      this.loadAttendances();
      return;
    }
    const shouldRecompute = RECOMPUTE_KEYS.some(k => changes[k] && !changes[k].firstChange);
    if (shouldRecompute) {
      this.recomputeDelayed();
      this.cdr.markForCheck();
    }
  }

  loadPage(page: number): void {
    this.pager.page = page;
    this.loadAttendances();
  }

  private loadAttendances(): void {
    this.loadingContent = true;
    const params = {
      ...(this.column.filterParams ?? {}),
      ...(this.channelFilter && { channel: this.channelFilter })
    };

    this.attendanceService.getMonitoringAttendances(this.pager.page, this.pager.perPage, null, params)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.loadingContent = false; this.cdr.markForCheck(); }),
      )
      .subscribe((pager: Pager<Attendance>) => {
        this.pager = this.pager.page === 1
          ? pager
          : { ...pager, list: [...(this.pager.list ?? []), ...(pager.list ?? [])] };

        this.recomputeDelayed();
      });
  }

  private recomputeDelayed(): void {
    this.delayedCount = (this.pager.list ?? []).reduce(
      (acc, a) => acc + (this.customerWaiting(a) > this.delayThresholdSeconds ? 1 : 0), 0,
    );

    this.metricsChanged.emit({
      type: this.column.type,
      columnKey: this.column.key,
      total: this.pager.total,
      delayed: this.delayedCount,
    });
  }

  private secondsSince(date: string | Date | undefined): number {
    return date ? Math.floor((Date.now() - new Date(date).getTime()) / 1000) : 0;
  }

  getCustomerWaitingSeconds(a: Attendance): number {
    return a.lastMessage?.user === null ? this.secondsSince(a.lastMessage?.createdAt) : 0;
  }

  getAttendantIdleSeconds(a: Attendance): number {
    return a.lastMessage && a.lastMessage.user !== null ? this.secondsSince(a.updatedAt) : 0;
  }

  private customerWaiting(a: Attendance): number {
    return this.getCustomerWaitingSeconds(a);
  }

  convertSecondsToTime(seconds: number): string {
    if (!seconds || seconds <= 0) return '';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(hrs)}:${pad(mins)}`;
  }

  getInitials(name?: string): string {
    if (!name?.trim()) return '?';
    const [first, , last] = name.trim().split(/\s+/).filter(Boolean).reduce<string[]>(
      (acc, p, i, arr) => (i === 0 || i === arr.length - 1 ? [...acc, p] : acc), [],
    );
    return ((first?.[0] ?? '') + (last?.[0] ?? '')).toUpperCase() || '?';
  }

  getChannelIcon(channel: Channel): string { return channelIconOf(channel); }
  getStatusLabel(s: AttendanceStatusEnum): string { return STATUS_LABELS[s] ?? s; }
  getStatusIcon(s: AttendanceStatusEnum): string { return STATUS_ICONS[s] ?? 'mdi mdi-help-circle-outline'; }

  getChannelNameFromItems(items: Attendance[]): string | undefined {
    return items.find(a => a.channel?.name)?.channel?.name;
  }

  getMessagePreview(item: Attendance): string {
    const msg = item?.lastMessage;
    if (!msg) return '';
    return MESSAGE_TYPE_LABELS[msg.type] ?? msg.content.replace(/_\*.*?\*_/g, '').trim();
  }

  goToAttendance(a: Attendance): void {
    if (!a) return;
    this.router.navigate(['/attendance/panel'], { queryParams: { id: a._id, status: a.status } });
  }
}
